import normalizeUrl from 'normalize-url'
import React, { useState } from 'react'

import { CopyButton } from '@components/common/CopyButton'
import { useDashboardFeatureFlags } from '@components/utils/DashboardFeatureFlags'
import { FeatureFlagDecorator } from '@components/utils/FeatureFlagDecorator'
import {
  Button,
  ButtonClean,
  ButtonGroup,
  Checkbox,
  Emoji,
  Fieldset,
  Heading,
  Input,
  Skeleton,
} from '@fourthwall/components'
import { useFormik } from '@fourthwall/utils/dist/formik'
import { TimezonedDatePicker } from '@modules/Timezone/components/TimezonedDatePicker'
import { reverse, routing } from '@utils/routing'

import { validationSchema } from './consts'
import * as S from './styled'
import type { SchedulerProps, SchedulerValues } from './types'

const renderDomain = (domain: string) => (
  <S.CopyLinkWrapper>
    <CopyButton
      label={normalizeUrl(domain, { stripProtocol: true, stripWWW: true })}
      href={normalizeUrl(domain)}
      copyLabel="Copy link"
      copyText={normalizeUrl(domain)}
    />
  </S.CopyLinkWrapper>
)

export const Scheduler = ({
  previewUrl,
  submitting,
  domain,
  onSchedule,
  onGoLive,
}: SchedulerProps) => {
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()
  const [previewLoaded, setPreviewLoaded] = useState(false)
  const [scheduleSelected, setScheduleSelected] = useState(false)
  const formik = useFormik<SchedulerValues>({
    initialValues: {
      liveFrom: undefined,
      addPassword: false,
      password: '',
    },
    validationSchema,
    onSubmit: (values) => onSchedule(values),
  })

  const renderDescription = () => {
    if (scheduleSelected) {
      return (
        <>
          Your site will show a coming soon page until the launch time.{' '}
          <ButtonClean
            label="Customize"
            to={reverse(routing.internal.customizeComingSoon, { pageId: 'password' })}
          />
        </>
      )
    }

    if (domain) {
      return <>You can preview your progress at {renderDomain(domain)}</>
    }

    return null
  }

  const renderTitle = () => {
    if (scheduleSelected) {
      return 'Schedule your launch'
    }

    return (
      <>
        You’re ready to launch! <Emoji shortcode="tada" />
      </>
    )
  }

  const renderHeading = () => (
    <div style={{ marginBottom: 20 }}>
      <Heading type="h4" weight="bold">
        {renderTitle()}
      </Heading>
      <S.Description>{renderDescription()}</S.Description>
    </div>
  )

  const renderContent = () => {
    if (scheduleSelected) {
      return (
        <div>
          <Fieldset>
            <Fieldset.Row>
              <Fieldset.Item>
                <TimezonedDatePicker
                  labels={['Launch date & time']}
                  showTimeInputs
                  minDate={new Date()}
                  value={[formik.getFieldProps('liveFrom').value]}
                  name={[formik.getFieldProps('liveFrom').name]}
                  error={formik.getFieldProps('liveFrom').error}
                  onChange={([date]) => {
                    formik.setFieldValue('liveFrom', date)
                  }}
                />
              </Fieldset.Item>
            </Fieldset.Row>
            <Fieldset.Row>
              <Fieldset.Item>
                <Checkbox
                  label="Add a password for early access"
                  {...formik.getFieldProps('addPassword')}
                />
              </Fieldset.Item>
            </Fieldset.Row>
            {formik.values.addPassword && (
              <Fieldset.Row>
                <Fieldset.Item>
                  <Input label="Password" {...formik.getFieldProps('password')} />
                </Fieldset.Item>
              </Fieldset.Row>
            )}
          </Fieldset>
        </div>
      )
    }

    return null
  }

  const renderButtons = () => {
    if (scheduleSelected) {
      return (
        <S.ScheduleButtonsContainer>
          <Button
            label="Schedule launch"
            appearance="primary"
            type="submit"
            disabled={submitting || !formik.dirty}
            fullWidth
          />
          <S.ButtonCleanContainer>
            or{` `}
            <ButtonClean label="go live now" disabled={submitting} onClick={onGoLive} />
          </S.ButtonCleanContainer>
        </S.ScheduleButtonsContainer>
      )
    }

    return (
      <div style={{ marginTop: 4 }}>
        <ButtonGroup direction="column" space="large">
          <Button label="Go live now" appearance="primary" fullWidth onClick={onGoLive} />
          <Button
            label="Schedule launch"
            appearance="secondary"
            fullWidth
            onClick={() => setScheduleSelected(true)}
          />
        </ButtonGroup>
      </div>
    )
  }

  return (
    <FeatureFlagDecorator {...dashboardFeatureFlags.homeScheduler}>
      <form onSubmit={formik.handleSubmit}>
        <S.Container>
          <S.PreviewContainer>
            <S.PreviewWrapper>
              {!previewLoaded && <Skeleton.Shape height="100%" />}
              <S.Iframe scrolling="no" src={previewUrl} onLoad={() => setPreviewLoaded(true)} />
            </S.PreviewWrapper>
          </S.PreviewContainer>
          <S.Main>
            {renderHeading()}
            {renderContent()}
            {renderButtons()}
          </S.Main>
        </S.Container>
      </form>
    </FeatureFlagDecorator>
  )
}
